// Small ownership adapter around Flink's existing shared player. Math Rods can
// keep its standalone Audio interface while native playback uses the same
// channel as Help, feedback and Add Audio. No new player or audio cache is made.
export function createSharedAudioHandle(source, {
  getPlayer, playSource, stopSource, publishPlaying = () => {},
  subscribe = () => () => {},
}) {
  let sound = null;
  let player = null;
  let phase = 'idle';
  let unsubscribe = null;
  const listeners = [];
  const handle = {
    src: source,
    onended: null,
    onerror: null,
    oninterrupted: null,
    onplay: null,
    onpause: null,
    get paused() { return phase !== 'playing'; },
    get currentTime() {
      const position = ownsSound() ? sound.seek?.() : 0;
      return typeof position === 'number' ? position : 0;
    },
    get duration() { return ownsSound() ? Number(sound.duration?.()) || 0 : 0; },
    play,
    pause,
    dispose,
  };

  function ownsSound() {
    return !!sound && getPlayer() === player && player.sound === sound
      && player.getCurrentSrc() === source;
  }
  function detach() {
    unsubscribe?.();
    unsubscribe = null;
    for (const [event, callback] of listeners) sound?.off?.(event, callback);
    listeners.length = 0;
  }
  function interrupted() {
    if (!['loading', 'playing', 'paused'].includes(phase)) return;
    phase = 'interrupted';
    detach();
    handle.oninterrupted?.();
  }
  function checkOwnership() {
    if (!ownsSound()) interrupted();
  }
  function listen(event, callback) {
    sound.on(event, callback);
    listeners.push([event, callback]);
  }
  function bind() {
    // Use the actual sound's events instead of options.onEnd: the shared
    // player's same-source replay path retains callbacks from its first load.
    listen('end', () => {
      if (!ownsSound()) { interrupted(); return; }
      if (!['loading', 'playing'].includes(phase)) return;
      phase = 'ended';
      detach();
      handle.onended?.();
    });
    const failed = (_id, error) => {
      if (!ownsSound()) { interrupted(); return; }
      if (!['loading', 'playing'].includes(phase)) return;
      phase = 'failed';
      detach();
      handle.onerror?.(error instanceof Error ? error : new Error(String(error || 'Audio could not play')));
    };
    listen('loaderror', failed);
    listen('playerror', failed);
    listen('play', () => {
      if (!ownsSound()) { interrupted(); return; }
      // A pause may happen while Howler is still loading. Flink's onload
      // callback starts playback, so reapply that deliberate pause here.
      if (phase === 'paused') { sound.pause(); publishPlaying(source, false); return; }
      if (phase !== 'loading' && phase !== 'playing') return;
      phase = 'playing';
      handle.onplay?.();
    });
    // stop()/unload() from Help, feedback, navigation or another native editor
    // must release Math Rods' interaction lock without finishing its answer.
    listen('stop', interrupted);
    unsubscribe = subscribe(checkOwnership);
  }
  function play() {
    if (phase === 'disposed') return Promise.reject(new Error('Audio handle is disposed'));
    try {
      if (phase === 'paused' && ownsSound()) {
        phase = 'playing';
        // Howler pause retains the seek position. The shared player's play()
        // resumes it and dispatches the normal global playback state.
        player.play();
        return Promise.resolve();
      }
      if (phase === 'playing' || phase === 'loading') {
        if (ownsSound()) return Promise.resolve();
        interrupted();
      }
      detach();
      player = getPlayer();
      if (!player?.playSrc || !player?.getCurrentSrc) throw new Error('The shared audio player is not ready');
      phase = 'loading';
      playSource(source);
      sound = player.sound;
      if (!sound?.on) throw new Error('The shared audio player did not create a sound');
      bind();
      if (!ownsSound()) interrupted();
      return Promise.resolve();
    } catch (error) {
      phase = 'failed';
      detach();
      return Promise.reject(error);
    }
  }
  function pause() {
    if (!['loading', 'playing'].includes(phase)) return;
    if (!ownsSound()) { interrupted(); return; }
    // Flink's stop() resets seek; pausing the shared Howl is the narrow bridge
    // needed to preserve the documented Go/Stop resume behavior.
    phase = 'paused';
    sound.pause();
    publishPlaying(source, false);
    handle.onpause?.();
  }
  function dispose() {
    if (phase === 'disposed') return;
    const stopOwned = ownsSound() && ['loading', 'playing', 'paused'].includes(phase);
    phase = 'disposed';
    detach();
    if (stopOwned) {
      // Desktop Flink starts sounds from its Howler onload callback. stop()
      // alone queues a stop but leaves that later automatic play intact. Remove
      // only this owned sound's pending load callbacks before releasing it;
      // explicit later playSrc/play requests still work on the loaded sound.
      if (sound.state?.() === 'loading') sound.off('load');
      stopSource(source);
    }
    sound = null;
  }
  return handle;
}

// Video retains the browser's native controls, but yields to the application's
// shared sound channel. Disposing one panel must never stop another panel.
export function coordinateSharedMediaElement(element, {
  getPlayer, stopSource, subscribe = () => () => {},
}) {
  if (!element) return () => {};
  const onPlay = () => stopSource();
  const onSharedChange = () => {
    if (!element.paused && getPlayer()?.getCurrentlyPlaying()) element.pause();
  };
  element.addEventListener('play', onPlay);
  const unsubscribe = subscribe(onSharedChange);
  return () => {
    element.removeEventListener('play', onPlay);
    unsubscribe();
    element.pause();
  };
}
